import { zodResolver } from '@hookform/resolvers/zod';
import { t, validationError } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import type { ApiTokenType } from '@repo/shared/schema';
import { Button } from '@repo/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@repo/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@repo/ui/form';
import { Input } from '@repo/ui/input';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { trpc } from '@/providers/trpc';
import { useOrganizationStore } from '@/stores/organization-store';

export type TokenDeleteDialogProps = {
  token: Pick<ApiTokenType, 'id' | 'name'>;
  onDelete?: () => void;
  children?: React.ReactNode;
};

export const TokenDeleteDialog = ({ token, onDelete, children }: TokenDeleteDialogProps) => {
  useLocale();

  const { currentOrganization } = useOrganizationStore();

  const [isOpen, setIsOpen] = useState(false);

  const deleteMessage = `DELETE ${token.name}` as const;

  const ZTokenDeleteDialogSchema = z.object({
    tokenName: z.literal(deleteMessage, {
      error: () => ({ message: t('common.deleteConfirmation', { value: deleteMessage }) }),
    }),
  });

  type TDeleteTokenByIdMutationSchema = z.infer<typeof ZTokenDeleteDialogSchema>;

  const { mutateAsync: deleteTokenMutation } = trpc.apiToken.deleteTokenById.useMutation({
    onSuccess() {
      onDelete?.();
    },
  });

  const form = useForm<TDeleteTokenByIdMutationSchema>({
    resolver: zodResolver(ZTokenDeleteDialogSchema, { error: validationError }),
    values: {
      tokenName: 'DELETE ',
    },
  });

  const onSubmit = async () => {
    try {
      await deleteTokenMutation({
        id: token.id,
        organizationId: currentOrganization?.id,
      });

      toast.success(t('workspace.tokenDeleted'), {
        get description() {
          return t('workspace.theTokenWasDeletedSuccessfully');
        },
      });

      setIsOpen(false);
    } catch (error) {
      toast.error(t('workspace.anUnknownErrorOccurred'), {
        get description() {
          return t('workspace.weEncounteredAnUnknownErrorWhileAttemptingToDelete');
        },
      });
    }
  };

  useEffect(() => {
    if (!isOpen) {
      form.reset();
    }
  }, [isOpen, form]);

  return (
    <Dialog
      onOpenChange={(value) => !form.formState.isSubmitting && setIsOpen(value)}
      open={isOpen}
    >
      <DialogTrigger asChild={true}>
        {children ?? (
          <Button className="me-4" variant="destructive">
            {t('workspace.delete')}{' '}
          </Button>
        )}
      </DialogTrigger>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('workspace.areYouSureYouWantToDeleteThisToken')}</DialogTitle>

          <DialogDescription>
            {t('workspace.pleaseNoteThatThisActionIsIrreversibleOnceConfirmed')}{' '}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <fieldset
              className="flex h-full flex-col space-y-4"
              disabled={form.formState.isSubmitting}
            >
              <FormField
                control={form.control}
                name="tokenName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('workspace.confirmByTyping')}{' '}
                      <span className="font-semibold font-sm text-destructive">
                        {deleteMessage}
                      </span>
                    </FormLabel>

                    <FormControl>
                      <Input className="bg-background" type="text" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <div className="flex w-full flex-nowrap gap-4">
                  <Button
                    className="flex-1"
                    onClick={() => setIsOpen(false)}
                    type="button"
                    variant="secondary"
                  >
                    {t('reports.cancel')}{' '}
                  </Button>

                  <Button
                    className="flex-1"
                    disabled={!form.formState.isValid}
                    loading={form.formState.isSubmitting}
                    type="submit"
                    variant="destructive"
                  >
                    {t('workspace.iMSureDeleteIt')}{' '}
                  </Button>
                </div>
              </DialogFooter>
            </fieldset>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
